import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";
import { WaIcon } from "./Logo";
import { waLink, waOpen, WHATSAPP_DISPLAY } from "../lib/site";
import { useContent } from "../lib/content";

const UNITS = ["Até 50", "51 a 100", "101 a 200", "201 a 300", "Mais de 300"];
const ROLES = ["Síndico(a)", "Conselheiro(a)", "Morador(a)", "Administradora", "Empresa (RH/facilities)", "Outro"];

const inputCls =
    "w-full rounded-xl border-2 border-[#1A231E]/12 bg-white px-4 py-3.5 text-base font-semibold text-[#1A231E] placeholder:text-[#1A231E]/35 placeholder:font-normal focus:outline-none focus:border-[#F57A3A] transition-colors";

export default function FormSolicitacao() {
    const content = useContent();
    const t = content.form;
    const [form, setForm] = useState({
        nome: "",
        condominio: "",
        cidade: "",
        unidades: UNITS[1],
        cargo: ROLES[0],
        obs: "",
    });
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const buildMessage = () =>
        [
            "Olá! Quero solicitar a implantação do minimercado autônomo Conbox no meu condomínio.",
            "",
            `Nome: ${form.nome}`,
            `Cargo: ${form.cargo}`,
            `Condomínio: ${form.condominio}`,
            `Bairro/Cidade: ${form.cidade}`,
            `Número de unidades: ${form.unidades}`,
            form.obs ? `Observações: ${form.obs}` : "",
        ]
            .filter(Boolean)
            .join("\n");

    const submit = (e) => {
        e.preventDefault();
        waOpen(buildMessage());
        setSent(true);
    };

    return (
        <section
            id="solicitacao"
            className="scroll-mt-24 bg-gradient-to-b from-[#0E6B3F] to-[#073A22] text-[#F7F4EC] py-20 sm:py-28"
        >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-start">
                <Reveal>
                    <SectionTag tone="orange">Solicitação de implantação</SectionTag>
                    <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
                        {t.title1}
                        <span className="font-caveat text-[#F1C75B] font-bold text-4xl sm:text-5xl inline-block mt-2 -rotate-1">
                            {t.title2}
                        </span>
                    </h2>
                    <p className="mt-5 text-base sm:text-lg text-[#F7F4EC]/80 leading-relaxed max-w-lg">
                        {t.subtitle}
                    </p>
                    <ul className="mt-8 space-y-3.5 text-base font-semibold text-[#F7F4EC]/85">
                        <li className="flex items-center gap-3">
                            <span className="w-2 h-2 rounded-full bg-[#F1C75B]" />
                            {t.b1}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="w-2 h-2 rounded-full bg-[#F1C75B]" />
                            {t.b2}
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="w-2 h-2 rounded-full bg-[#F1C75B]" />
                            {t.b3}
                        </li>
                    </ul>
                </Reveal>

                <Reveal delay={0.1}>
                    <form
                        onSubmit={submit}
                        data-testid="form-solicitacao"
                        className="rounded-[2rem] bg-[#F7F4EC] text-[#1A231E] p-7 sm:p-9 sticker -rotate-1"
                    >
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="f-nome">
                                    Seu nome *
                                </label>
                                <input
                                    id="f-nome"
                                    data-testid="form-name-input"
                                    required
                                    value={form.nome}
                                    onChange={set("nome")}
                                    placeholder="Maria Silva"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="f-cargo">
                                    Seu cargo *
                                </label>
                                <select
                                    id="f-cargo"
                                    data-testid="form-role-select"
                                    value={form.cargo}
                                    onChange={set("cargo")}
                                    className={inputCls}
                                >
                                    {ROLES.map((r) => (
                                        <option key={r}>{r}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="f-unidades">
                                    Nº de unidades *
                                </label>
                                <select
                                    id="f-unidades"
                                    data-testid="form-units-select"
                                    value={form.unidades}
                                    onChange={set("unidades")}
                                    className={inputCls}
                                >
                                    {UNITS.map((u) => (
                                        <option key={u}>{u}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="f-cond">
                                    Condomínio ou empresa *
                                </label>
                                <input
                                    id="f-cond"
                                    data-testid="form-condo-input"
                                    required
                                    value={form.condominio}
                                    onChange={set("condominio")}
                                    placeholder="Residencial Jardins do Sol / Empresa XYZ"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="f-cidade">
                                    Bairro / Cidade *
                                </label>
                                <input
                                    id="f-cidade"
                                    data-testid="form-city-input"
                                    required
                                    value={form.cidade}
                                    onChange={set("cidade")}
                                    placeholder="Maceió — AL"
                                    className={inputCls}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-bold uppercase tracking-wider text-[#1A231E]/60 mb-1.5" htmlFor="f-obs">
                                    Observações (opcional)
                                </label>
                                <textarea
                                    id="f-obs"
                                    data-testid="form-obs-input"
                                    rows={3}
                                    value={form.obs}
                                    onChange={set("obs")}
                                    placeholder="Ex.: temos um espaço livre na garagem..."
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="form-submit-whatsapp-button"
                            className="mt-6 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#25D366] hover:bg-[#1FB457] text-[#073A22] font-extrabold px-6 py-4.5 py-4 text-base transition-all duration-200 hover:-translate-y-0.5"
                        >
                            <WaIcon className="w-5 h-5" />
                            Enviar solicitação no WhatsApp
                        </button>

                        {sent && (
                            <div
                                data-testid="form-success-message"
                                className="mt-4 rounded-xl border-2 border-[#0E6B3F]/30 bg-[#0E6B3F]/10 px-4 py-3.5 flex items-start gap-3"
                            >
                                <CheckCircle2 className="w-5 h-5 text-[#0E6B3F] shrink-0 mt-0.5" />
                                <p className="text-sm font-semibold text-[#0E6B3F]">
                                    Solicitação pronta! Se o WhatsApp não abriu,{" "}
                                    <a href={waLink(buildMessage())} target="_blank" rel="noopener noreferrer" className="underline font-extrabold">
                                        toque aqui para enviar
                                    </a>{" "}
                                    para {content.whatsapp.display}.
                                </p>
                            </div>
                        )}
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
